import React, { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'
import api from '../services/api'
import { Button } from './ui'
import './EntityHistory.css'

const ACTION_LABELS = {
  create: 'Создан',
  update: 'Изменён',
  delete: 'Удалён'
}

const ENTITY_TYPE_LABELS = {
  contracts: 'Договор',
  stages: 'Стадия',
  acts: 'Акт'
}

function formatDateTime(iso) {
  if (!iso) return '—'
  try {
    const d = new Date(iso)
    return d.toLocaleString('ru-RU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  } catch {
    return iso
  }
}

function formatDetailValue(v) {
  if (v === null || v === undefined) return '—'
  if (typeof v === 'object') return JSON.stringify(v)
  return String(v)
}

function ChangesCell({ oldValues, newValues, action, changesSummary }) {
  const [expanded, setExpanded] = useState(false)
  let summary = ''
  let hasDetails = false
  let updateDiffKeys = []
  if (changesSummary != null && changesSummary !== '') {
    summary = changesSummary
    hasDetails = false
  } else {
  try {
    const oldObj = typeof oldValues === 'string' ? JSON.parse(oldValues || '{}') : (oldValues || {})
    const newObj = typeof newValues === 'string' ? JSON.parse(newValues || '{}') : (newValues || {})
    if (action === 'create' && newObj && typeof newObj === 'object') {
      const keys = Object.keys(newObj).filter(k => !/^(created_at|updated_at|id)$/.test(k))
      hasDetails = keys.length > 0
      summary = keys.length ? `поля: ${keys.slice(0, 3).join(', ')}${keys.length > 3 ? '…' : ''}` : '—'
    } else if (action === 'update' && oldObj && typeof oldObj === 'object') {
      updateDiffKeys = Object.keys(oldObj)
      hasDetails = updateDiffKeys.length > 0
      summary = updateDiffKeys.length ? `изменено полей: ${updateDiffKeys.length}` : '—'
    } else if (action === 'delete' && oldObj && typeof oldObj === 'object') {
      const keys = Object.keys(oldObj).filter(k => k !== 'password_hash' && k !== 'password')
      hasDetails = keys.length > 0
      summary = keys.length ? 'удалена запись' : '—'
    }
  } catch {
    summary = '—'
  }
  }

  const renderDetail = () => {
    if (action === 'update' && updateDiffKeys.length > 0) {
      const oldObj = typeof oldValues === 'string' ? JSON.parse(oldValues || '{}') : (oldValues || {})
      return (
        <div className="entity-history-detail">
          <strong>Изменения:</strong>
          <ul className="entity-history-diff-list">
            {updateDiffKeys.map((key) => {
              const cell = oldObj[key]
              const oldVal = cell && typeof cell === 'object' && 'old' in cell ? cell.old : cell
              const newVal = cell && typeof cell === 'object' && 'new' in cell ? cell.new : cell
              return (
                <li key={key}>
                  <strong>{key}</strong>: {formatDetailValue(oldVal)} → {formatDetailValue(newVal)}
                </li>
              )
            })}
          </ul>
        </div>
      )
    }
    return (
      <div className="entity-history-detail">
        {oldValues && (
          <div>
            <strong>Было:</strong>
            <pre>{typeof oldValues === 'string' ? oldValues : JSON.stringify(oldValues, null, 2)}</pre>
          </div>
        )}
        {newValues && action !== 'update' && (
          <div>
            <strong>Стало:</strong>
            <pre>{typeof newValues === 'string' ? newValues : JSON.stringify(newValues, null, 2)}</pre>
          </div>
        )}
      </div>
    )
  }

  return (
    <td className="entity-history-changes">
      <span className="entity-history-summary">{summary}</span>
      {hasDetails && (
        <Button
          type="button"
          variant="ghost"
          className="entity-history-toggle"
          onClick={() => setExpanded(!expanded)}
          aria-expanded={expanded}
        >
          {expanded ? 'Свернуть' : 'Подробнее'}
        </Button>
      )}
      {expanded && (oldValues || newValues) && renderDetail()}
    </td>
  )
}

export default function EntityHistory({ entityType, entityId, title = 'История изменений' }) {
  const { user } = useAuth()
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  const isAdmin = user?.role === 'admin'
  const hasEntityId = entityId != null && entityId !== ''
  const showEntityTypeColumn = entityType === 'contracts' && hasEntityId

  useEffect(() => {
    if (!entityType || !isAdmin) {
      setLogs([])
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)
    setError(null)
    const params = { entity_type: entityType }
    if (hasEntityId) params.entity_id = entityId
    api.get('/company/change-logs', { params })
      .then((res) => {
        if (!cancelled && res.data) {
          setLogs(Array.isArray(res.data.change_logs) ? res.data.change_logs : [])
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.response?.data?.error || err.message || 'Ошибка загрузки истории')
          setLogs([])
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => { cancelled = true }
  }, [entityType, entityId, isAdmin])

  if (!isAdmin || !entityType) return null

  return (
    <section className="entity-history-section entity-history-section--flow card-section section-card" aria-label={title || 'История изменений'}>
      {title ? <h2 className="ds-section-heading entity-history-title">{title}</h2> : null}
      {loading && <div className="entity-history-loading">Загрузка…</div>}
      {error && <div className="entity-history-error" role="alert">{error}</div>}
      {!loading && !error && logs.length === 0 && (
        <p className="entity-history-empty">Изменений пока нет.</p>
      )}
      {!loading && !error && logs.length > 0 && (
        <div className="table-responsive entity-history-table-wrap">
          <table className="ui-table data-table entity-history-table">
            <thead>
              <tr>
                <th>Дата</th>
                <th>Пользователь</th>
                {showEntityTypeColumn && <th>Сущность</th>}
                {!hasEntityId && <th>Объект</th>}
                <th>Действие</th>
                <th>Изменения</th>
              </tr>
            </thead>
            <tbody>
              {logs.map((log) => (
                <tr key={log.id}>
                  <td>{formatDateTime(log.created_at)}</td>
                  <td>{log.user_display_name || '—'}</td>
                  {showEntityTypeColumn && (
                    <td>{ENTITY_TYPE_LABELS[log.entity_type] || log.entity_type || '—'}</td>
                  )}
                  {!hasEntityId && (
                    <td className="entity-history-entity-id" title={log.entity_id}>
                      {log.display_summary != null ? log.display_summary : (log.entity_id ? (log.entity_id.length > 20 ? log.entity_id.slice(0, 20) + '…' : log.entity_id) : '—')}
                    </td>
                  )}
                  <td>
                    <span className={`entity-history-action entity-history-action--${log.action}`}>
                      {ACTION_LABELS[log.action] || log.action}
                    </span>
                  </td>
                  <ChangesCell
                    oldValues={log.old_values}
                    newValues={log.new_values}
                    action={log.action}
                    changesSummary={log.changes_summary}
                  />
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
